import {
  Component,
  ElementRef,
  Inject,
  Injector,
  NgZone,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { NB_WINDOW_CONTEXT } from "@nebular/theme";
import { OverlayShellConfig, OverlayShellContext } from "./overlay.models";

type ResizeDirection = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

@Component({
  selector: "overlay-shell",
  templateUrl: "./overlay-shell.component.html",
  styleUrls: ["./overlay-shell.component.scss"],
})
export class OverlayShellComponent {
  @ViewChild("root", { static: true }) root!: ElementRef<HTMLElement>;

  public componentInjector!: Injector;

  private config!: OverlayShellConfig;
  private nbWindowEl?: HTMLElement;
  private initialWidth: number = 0;
  private initialHeight: number = 0;
  private handles: HTMLElement[] = [];

  private resizeObserver!: ResizeObserver;
  private viewportObserver!: ResizeObserver;

  private dragging = false;
  private resizing = false;

  private removeResizeMove?: () => void;
  private removeResizeUp?: () => void;
  private removeDragMove?: () => void;
  private removeDragUp?: () => void;

  constructor(
    @Inject(NB_WINDOW_CONTEXT) public context: OverlayShellContext,
    private parentInjector: Injector,
    private renderer: Renderer2,
    private zone: NgZone,
  ) {
    this.config = context.config;
  }

  ngOnInit(): void {
    this.componentInjector = Injector.create({
      parent: this.parentInjector,
      providers: [
        {
          provide: NB_WINDOW_CONTEXT,
          useValue: this.context.componentContext,
        },
      ],
    });
  }

  ngAfterViewInit(): void {
    this.nbWindowEl = this.root.nativeElement.closest("nb-window");
    this.setInitialSize();
    this.center();
    this.setResizeObserver();
    this.setViewportObserver();
    this.attachDragHandle();
    if (this.config.resizable) {
      this.attachResizeHandles();
    }
  }

  private setInitialSize() {
    this.nbWindowEl.style.width = this.config.width;
    this.nbWindowEl.style.height = this.config.height;

    const rect = this.nbWindowEl.getBoundingClientRect();
    this.initialWidth = rect.width;
    this.initialHeight = rect.height;
  }

  private center() {
    const el = this.nbWindowEl;

    const rect = el.getBoundingClientRect();

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const left = Math.round((vw - rect.width) / 2);
    const top = Math.round((vh - rect.height) / 2);

    el.style.transform = "none";
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }

  private setResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      if (this.dragging || this.resizing) return;
      this.center();
    });

    this.resizeObserver.observe(this.root.nativeElement);
  }

  private setViewportObserver() {
    this.viewportObserver = new ResizeObserver(() => {
      this.center();
    });

    this.viewportObserver.observe(document.documentElement);
  }

  private attachResizeHandles() {
    const directions: ResizeDirection[] = [
      "n",
      "s",
      "e",
      "w",
      "ne",
      "nw",
      "se",
      "sw",
    ];
    for (const direction of directions) {
      const handle = this.renderer.createElement("div") as HTMLElement;

      this.renderer.addClass(handle, "nb-window__resize-handle");
      this.renderer.addClass(handle, `nb-window__resize-handle--${direction}`);

      this.renderer.listen(handle, "pointerdown", (event: PointerEvent) =>
        this.startResize(event, direction),
      );

      this.nbWindowEl!.appendChild(handle);
      this.handles.push(handle);
    }
  }

  private attachDragHandle() {
    const header = this.nbWindowEl.querySelector("nb-card-header");
    this.renderer.listen(header, "pointerdown", (event: PointerEvent) =>
      this.startDrag(event),
    );
    this.renderer.setStyle(header, "cursor", "move");
    this.renderer.setStyle(header, "user-select", "none");
    this.renderer.setStyle(header, "touch-action", "none");
  }

  private startResize(event: PointerEvent, direction: ResizeDirection) {
    if (this.resizing || this.dragging) return;

    event.preventDefault();
    event.stopPropagation();
    document.body.dispatchEvent(new MouseEvent("mousedown", { bubbles: true })); // close any open ng-select dropdowns

    const startX = event.clientX;
    const startY = event.clientY;

    const rect = this.nbWindowEl.getBoundingClientRect();
    const startLeft = rect.left;
    const startTop = rect.top;
    const startW = rect.width;
    const startH = rect.height;

    const minW = this.initialWidth;
    const minH = this.initialHeight;
    const maxW = window.innerWidth;
    const maxH = window.innerHeight;

    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    this.resizing = true;

    this.zone.runOutsideAngular(() => {
      const onMove = (e: PointerEvent) => {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        let nextLeft = startLeft;
        let nextTop = startTop;
        let nextW = startW;
        let nextH = startH;

        if (direction.includes("e")) {
          nextW = startW + dx;
          const maxWE = window.innerWidth - startLeft;
          nextW = this.clamp(nextW, minW, Math.min(maxW, maxWE));
          nextLeft = startLeft;
        }

        if (direction.includes("w")) {
          nextW = startW - dx;
          const maxWW = startLeft + startW;
          nextW = this.clamp(nextW, minW, Math.min(maxW, maxWW));
          nextLeft = startLeft + (startW - nextW);
        }

        if (direction.includes("s")) {
          nextH = startH + dy;
          const maxHS = window.innerHeight - startTop;
          nextH = this.clamp(nextH, minH, Math.min(maxH, maxHS));
          nextTop = startTop;
        }

        if (direction.includes("n")) {
          nextH = startH - dy;
          const maxHN = startTop + startH;
          nextH = this.clamp(nextH, minH, Math.min(maxH, maxHN));
          nextTop = startTop + (startH - nextH);
        }

        this.nbWindowEl.style.left = `${Math.round(nextLeft)}px`;
        this.nbWindowEl.style.top = `${Math.round(nextTop)}px`;
        this.nbWindowEl.style.width = `${Math.round(nextW)}px`;
        this.nbWindowEl.style.height = `${Math.round(nextH)}px`;
      };

      const onUp = () => this.detachResize();

      window.addEventListener("pointermove", onMove, { passive: true });
      window.addEventListener("pointerup", onUp, { passive: true, once: true });

      this.removeResizeMove = () =>
        window.removeEventListener("pointermove", onMove);
      this.removeResizeUp = () => window.removeEventListener("pointerup", onUp);
    });
  }

  private startDrag(event: PointerEvent) {
    if (this.resizing || this.dragging) return;

    event.preventDefault();
    event.stopPropagation();
    document.body.dispatchEvent(new MouseEvent("mousedown", { bubbles: true })); // close any open ng-select dropdowns

    const startX = event.clientX;
    const startY = event.clientY;

    const rect = this.nbWindowEl.getBoundingClientRect();
    const startLeft = rect.left;
    const startTop = rect.top;

    const w = rect.width;
    const h = rect.height;

    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    this.dragging = true;

    this.zone.runOutsideAngular(() => {
      const onMove = (e: PointerEvent) => {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        let nextLeft = startLeft + dx;
        let nextTop = startTop + dy;

        nextLeft = this.clamp(nextLeft, 0, window.innerWidth - w);
        nextTop = this.clamp(nextTop, 0, window.innerHeight - h);

        this.nbWindowEl!.style.left = `${Math.round(nextLeft)}px`;
        this.nbWindowEl!.style.top = `${Math.round(nextTop)}px`;
      };

      const onUp = () => this.detachDrag();

      window.addEventListener("pointermove", onMove, { passive: true });
      window.addEventListener("pointerup", onUp, { passive: true, once: true });

      this.removeDragMove = () =>
        window.removeEventListener("pointermove", onMove);
      this.removeDragUp = () => window.removeEventListener("pointerup", onUp);
    });
  }

  private detachResize() {
    this.removeResizeMove?.();
    this.removeResizeUp?.();
    this.removeResizeMove = undefined;
    this.removeResizeUp = undefined;
    this.resizing = false;
  }

  private detachDrag() {
    this.removeDragMove?.();
    this.removeDragUp?.();
    this.removeDragMove = undefined;
    this.removeDragUp = undefined;
    this.dragging = false;
  }

  private clamp(v: number, min: number, max: number) {
    return Math.max(min, Math.min(max, v));
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
    this.viewportObserver.disconnect();
  }
}
